import { useCallback, useEffect, useMemo } from "react";

import useNotification from "components/Notification/useNotification";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import useAuthentication from "hooks/useAuthentication";
import { fetchApi } from "utils/baseFetcher";

type TopInvestorQuestionItem = {
  id: number;
  entity_id: number;
  entity_name: string;
  entity_ticker: string;
  status: "queued" | "processing" | "completed" | "failed";
  briefing_json?: Record<string, any>;
  error_message?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
};

type UseTopInvestorQuestionsRequestProps = {
  entity?: {
    id?: number | string;
    prettyName?: string;
    uniqueTicker?: string;
    security?: string;
  };
  items: TopInvestorQuestionItem[];
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => Promise<any>;
  onRequireUpgrade: () => void;
  shouldAutoGenerate?: boolean;
};

export const useTopInvestorQuestionsRequest = ({
  entity,
  items,
  isLoading,
  isValidating,
  mutate,
  onRequireUpgrade,
  shouldAutoGenerate = false,
}: UseTopInvestorQuestionsRequestProps) => {
  const { authenticated, account } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const { setNotification } = useNotification();

  const canCreateManagementQuestions = useMemo(() => {
    if (!authenticated || !account) return false;

    return account.isClientOrIP && !account.isPreviewPass;
  }, [account, authenticated]);

  const latestCompletedQuestion = useMemo(() => {
    return items.find((item) => item.status === "completed");
  }, [items]);

  const hasFreshQuestion = items.length > 0;

  const onGenerate = useCallback(async () => {
    if (!authenticated) {
      toggleModal("sign-up");
      return;
    }

    if (!canCreateManagementQuestions) {
      onRequireUpgrade();
      return;
    }

    if (hasFreshQuestion) {
      return;
    }

    if (!entity?.id) {
      setNotification("Unable to generate Meeting Prep", "error");
      return;
    }

    const payload = {
      entity_id: entity.id,
      entity_name: entity.prettyName,
      entity_ticker: entity.uniqueTicker || entity.security,
    };

    const response = await fetchApi("/api/v3/top-investor-questions", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response?.errors?.length) {
      if (response?.errors?.[0]?.title === "Record conflict") return;

      setNotification(
        response?.errors?.[0]?.title || "Unable to generate Meeting Prep",
        "error"
      );
      return;
    }

    await mutate();
  }, [
    authenticated,
    canCreateManagementQuestions,
    entity,
    hasFreshQuestion,
    mutate,
    onRequireUpgrade,
    setNotification,
    toggleModal,
  ]);

  useEffect(() => {
    if (!shouldAutoGenerate) return;
    if (!entity?.id) return;
    if (isLoading || isValidating) return;

    if (hasFreshQuestion) {
      return;
    }

    void onGenerate();
  }, [
    entity?.id,
    hasFreshQuestion,
    isLoading,
    isValidating,
    onGenerate,
    shouldAutoGenerate,
  ]);

  return {
    canCreateManagementQuestions,
    latestCompletedQuestion,
    onGenerate,
  };
};
